// src/components/PaymentList.tsx
import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent, Button } from './ui';
import { Plus, Edit, Trash2 } from 'lucide-react';
import PaymentForm from './PaymentForm';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";

// 添加 Year 接口定义
interface Year {
  id: number;
  short_name: string;
  full_name: string;
}

interface Payment {
  id: number;
  person_id: number;
  year: number;
  payment_date: string;
  amount: number;
  book_number: string;    // 新增字段
  receipt_number: string; // 新增字段
}

interface PaymentListProps {
  studentId: number;
  studentName?: string;
}

const PaymentList: React.FC<PaymentListProps> = ({ studentId, studentName }) => {
  const [payments, setPayments] = useState<Payment[]>([]);
  const [years, setYears] = useState<Year[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isPaymentFormOpen, setIsPaymentFormOpen] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState<Payment | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [paymentToDelete, setPaymentToDelete] = useState<number | null>(null);

  // 添加获取年度列表的函数
  const fetchYears = async () => {
    try {
      const response = await fetch('/api/years');
      if (response.ok) {
        const data = await response.json();
        setYears(data);
      }
    } catch (error) {
      console.error('Error fetching years:', error);
    }
  };

  const fetchPayments = async () => {
    try {
      setLoading(true);
      const response = await fetch(`/api/students/${studentId}/payments`);
      if (!response.ok) {
        throw new Error('Failed to fetch payments');
      }
      const data = await response.json();
      setPayments(data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      console.error('Error fetching payments:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchYears();
    fetchPayments();
  }, [studentId]);

  // 修改年度 ID 到年度名称的转换函数，增加边界条件处理
  const getYearName = (yearId: number | null): string => {
    if (yearId === null || yearId === undefined) {
      return '未知年度';
    }
    const year = years.find(y => y.id === yearId);
    return year ? year.full_name : `${yearId}年度`;
  };

  const handleEdit = (payment: Payment) => {
    setSelectedPayment(payment);
    setIsPaymentFormOpen(true);
  };

  const handleDeleteClick = (paymentId: number) => {
    setPaymentToDelete(paymentId);
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!paymentToDelete) return;
    
    try {
      const response = await fetch(`/api/students/${studentId}/payments/${paymentToDelete}`, {
        method: 'DELETE',
      });
      
      if (response.ok) {
        fetchPayments();
      } else {
        throw new Error('删除失败');
      }
    } catch (err) {
      console.error('Error deleting payment:', err);
      alert('删除失败，请重试');
    } finally {
      setIsDeleteDialogOpen(false);
      setPaymentToDelete(null);
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle>缴费记录</CardTitle>
          {studentName && <p className="text-sm text-gray-500 mt-1">林员：{studentName}</p>}
        </div>
        <Button
          onClick={() => setIsPaymentFormOpen(true)}
          className="bg-blue-500 hover:bg-blue-600"
        >
          <Plus className="w-4 h-4 mr-2" />
          新增缴费
        </Button>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="text-center py-4">加载中...</div>
        ) : error ? (
          <div className="text-red-500 text-center py-4">
            Error: {error}
            <Button
              onClick={fetchPayments}
              className="ml-2 bg-blue-500 hover:bg-blue-600"
            >
              重试
            </Button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="p-2 text-left">年度</th>
                  <th className="p-2 text-left">缴费日期</th>
                  <th className="p-2 text-left">分册编号</th>
                  <th className="p-2 text-left">凭证编号</th>
                  <th className="p-2 text-right">金额</th>
                  <th className="p-2 text-center">操作</th>
                </tr>
              </thead>
              <tbody>
                {payments.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="text-center py-4">暂无缴费记录</td>
                  </tr>
                ) : (
                  payments
                    // Sort payments by year in descending order (newest years first)
                    // This ensures 2025年度 appears at the top, followed by 2024年度, etc.
                    .sort((a, b) => b.year - a.year)
                    .map((payment) => (
                    <tr key={payment.id} className="border-b hover:bg-gray-50">
                      <td className="p-2">{getYearName(payment.year)}</td>
                      <td className="p-2">{payment.payment_date || '-'}</td>
                      <td className="p-2">{payment.book_number || '-'}</td>
                      <td className="p-2">{payment.receipt_number || '-'}</td>
                      <td className="p-2 text-right">
                        ¥{(payment.amount || 0).toFixed(2)}
                      </td>
                      <td className="p-2 text-center">
                        <div className="flex justify-center space-x-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEdit(payment)}
                            className="h-8 w-8 p-0"
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteClick(payment.id)}
                            className="h-8 w-8 p-0 text-red-500 hover:text-red-700"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
              {payments.length > 0 && (
                <tfoot>
                  <tr className="font-semibold">
                    <td colSpan={4} className="p-2 text-right">总计：</td>
                    <td className="p-2 text-right">
                      ¥{payments.reduce((sum, payment) => sum + payment.amount, 0).toFixed(2)}
                    </td>
                    <td></td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        )}
      </CardContent>

      <PaymentForm
        studentId={studentId}
        payment={selectedPayment}
        isOpen={isPaymentFormOpen}
        onClose={() => {
          setIsPaymentFormOpen(false);
          setSelectedPayment(null);
        }}
        onSuccess={fetchPayments}
      />

      {/* Custom Delete Dialog using existing Dialog component */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>确认删除</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p>您确定要删除这条缴费记录吗？此操作无法撤销。</p>
          </div>
          <DialogFooter className="flex justify-end space-x-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsDeleteDialogOpen(false)}
            >
              取消
            </Button>
            <Button
              type="button"
              onClick={handleDeleteConfirm}
              className="bg-red-500 hover:bg-red-600"
            >
              删除
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default PaymentList;